import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip,
    ReferenceArea, ReferenceLine, ResponsiveContainer, Cell, LabelList,
} from "recharts";
import { GripVertical, Download, HelpCircle, ChevronDown, CheckCircle2, ArrowRight } from "lucide-react";
import FunderShell from "./FunderShell";
import { getCommittee } from "../../service/FunderPortalService";
import { SectionCard, MetricCard, ScorePill, StageLabel, toneColor, scoreColor } from "./FunderUI";

// Consensus-map quadrant colors (avg reviewer score × score variance).
// Variance is treated as signal, not error: high spread flags a proposal
// worth the committee's discussion, and reviewer agreement is not a funding
// decision on its own.
const QUAD = { discuss: "#d4a853", aligned: "#5b8fa8", divergent: "#9b7bb8", lowAligned: "#8a958d" };
function quadColor(p) {
    if (p.avg >= 6.5 && p.variance >= 2) return QUAD.discuss;
    if (p.avg >= 6.5 && p.variance < 2) return QUAD.aligned;
    if (p.avg < 6.5 && p.variance >= 2) return QUAD.divergent;
    return QUAD.lowAligned;
}

function ConsensusTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 11px", fontSize: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}>
            <div style={{ fontWeight: 700, color: "var(--ink-900)" }}>{d.grantNumber}</div>
            <div style={{ color: "var(--ink-500)", marginTop: 2 }}>Avg {d.avg.toFixed(1)} · Variance {d.variance.toFixed(1)}</div>
        </div>
    );
}

const TIER_META = {
    shortlist: { label: "Shortlist for interview", color: "var(--success)" },
    discuss: { label: "Discuss further", color: "#c5862f" },
    reject: { label: "Not progressing", color: "#8a958d" },
};

export default function FunderCommittee() {
    return (
        <FunderShell activePage="Committee">
            {(callId, call) => <Committee callId={callId} call={call} />}
        </FunderShell>
    );
}

function Committee({ callId, call }) {
    const navigate = useNavigate();
    const data = getCommittee(callId);

    // Shortlist-builder tiers live in local state (drag-and-drop, demo only).
    const [tiers, setTiers] = useState(() => ({
        shortlist: data ? [...data.shortlist.shortlist] : [],
        discuss: data ? [...data.shortlist.discuss] : [],
        reject: data ? [...data.shortlist.reject] : [],
    }));
    const [dragged, setDragged] = useState(null); // {from, grantNumber}
    const [dropTier, setDropTier] = useState(null);
    const [openBriefing, setOpenBriefing] = useState(data && data.briefings.length ? data.briefings[0].id : null);

    if (!data) return <div className="funder-empty">No committee data for this call.</div>;

    const sumAssigned = data.members.reduce((s, m) => s + m.assigned, 0);
    const sumCompleted = data.members.reduce((s, m) => s + m.completed, 0);
    const completionPct = sumAssigned ? Math.round((sumCompleted / sumAssigned) * 100) : 0;
    const ratio = (m) => (m.assigned ? m.completed / m.assigned : 0);
    const onTrack = data.members.filter((m) => ratio(m) > 0.5).length;
    const behind = data.members.filter((m) => ratio(m) <= 0.5).length;

    const hiddenReject = data.shortlist.hiddenReject || 0;
    const totalRanked = tiers.shortlist.length + tiers.discuss.length + tiers.reject.length + hiddenReject;
    const shortlistAvg = tiers.shortlist.length
        ? tiers.shortlist.reduce((s, c) => s + c.committee, 0) / tiers.shortlist.length
        : 0;
    const shortlistPct = totalRanked ? Math.round((tiers.shortlist.length / totalRanked) * 100) : 0;

    function moveCard(grantNumber, from, to) {
        if (from === to) return;
        setTiers((prev) => {
            const card = prev[from].find((c) => c.grantNumber === grantNumber);
            if (!card) return prev;
            return {
                ...prev,
                [from]: prev[from].filter((c) => c.grantNumber !== grantNumber),
                [to]: [...prev[to], card],
            };
        });
    }

    const memberStatus = (m) => {
        const r = ratio(m);
        if (r >= 1) return { label: "Complete", color: "var(--success)", bg: "var(--success-bg)" };
        if (r <= 0.5) return { label: "Behind", color: "var(--warning)", bg: "var(--warning-bg)" };
        return { label: "On track", color: "var(--info)", bg: "var(--info-bg)" };
    };

    return (
        <div className="funder-section-stack">
            <StageLabel>Stages 5–6 · Committee review &amp; decision preparation</StageLabel>

            {/* 1. Committee Progress ------------------------------------------------ */}
            <SectionCard kicker="Committee Progress" title="Committee Progress"
                subtitle={`Tracking committee members' review progress for ${call.round}.`}>
                <div className="funder-metric-grid funder-metric-grid-3" style={{ marginBottom: 18 }}>
                    <MetricCard label="Committee Completion" value={`${sumCompleted}/${sumAssigned}`}
                        sub={`reviews · across ${data.members.length} committee members`}>
                        <div className="funder-bar-track" style={{ marginTop: 8 }}>
                            <div className="funder-bar-fill" style={{ width: `${completionPct}%`, background: "var(--success)" }} />
                        </div>
                    </MetricCard>
                    <MetricCard label="Days Remaining" value={data.daysRemaining > 0 ? `${data.daysRemaining}` : "—"}
                        sub={data.daysRemaining > 0 ? `days · Panel meeting ${data.panelDate}` : `Round closed · panel met ${data.panelDate}`} />
                    <MetricCard label="Members On Track" value={`${onTrack}/${data.members.length}`}
                        sub={`${behind} member${behind === 1 ? "" : "s"} below 50% completion`} />
                </div>

                <div className="funder-kicker">Member Status</div>
                <div style={{ overflowX: "auto" }}><table className="funder-table">
                    <thead>
                        <tr><th>Member</th><th>Assigned</th><th>Completed</th><th>Avg Score</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                        {data.members.map((m) => {
                            const s = memberStatus(m);
                            return (
                                <tr key={m.name}>
                                    <td style={s.label === "Behind" ? { borderLeft: "3px solid #c5862f", fontWeight: 600 } : { fontWeight: 600 }}>{m.name}</td>
                                    <td>{m.assigned}</td>
                                    <td>{m.completed}</td>
                                    <td>{m.avg.toFixed(1)}</td>
                                    <td><span className="status-badge" style={{ background: s.bg, color: s.color }}>{s.label}</span></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table></div>
            </SectionCard>

            {/* 2. Panel Overview --------------------------------------------------- */}
            <SectionCard kicker="Panel Overview" title="Where the committee agrees and disagrees"
                subtitle={`${call.round} · Panel meeting: ${data.panelDate}`}>
                <div style={{ background: "var(--surface-2)", border: "1px solid var(--border-subtle)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: "var(--ink-600)" }}>
                    <div><strong style={{ color: "var(--ink-900)" }}>Expert reviews received:</strong> {data.expertReviewStatus.received} of {data.expertReviewStatus.total} shortlisted proposals covered</div>
                    <div style={{ marginTop: 4 }}><strong style={{ color: "var(--ink-900)" }}>Outstanding:</strong> {data.expertReviewStatus.pending} proposals awaiting expert review — {data.expertReviewStatus.invitationsPending} invitations pending response</div>
                </div>

                <div className="funder-kicker">Consensus Map</div>
                <div className="funder-scatter-box" style={{ width: "100%", height: 360 }}>
                    <ResponsiveContainer>
                        <ScatterChart margin={{ top: 16, right: 24, bottom: 28, left: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
                            <ReferenceArea x1={6.5} x2={10} y1={2} y2={4} fill={QUAD.discuss} fillOpacity={0.1} />
                            <ReferenceArea x1={6.5} x2={10} y1={0} y2={2} fill={QUAD.aligned} fillOpacity={0.1} />
                            <ReferenceArea x1={1} x2={6.5} y1={2} y2={4} fill={QUAD.divergent} fillOpacity={0.14} />
                            <ReferenceArea x1={1} x2={6.5} y1={0} y2={2} fill={QUAD.lowAligned} fillOpacity={0.08} />
                            <ReferenceLine x={6.5} stroke="var(--border-strong)" />
                            <ReferenceLine y={2} stroke="var(--border-strong)" />
                            <XAxis type="number" dataKey="avg" domain={[1, 10]} ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]} tick={{ fontSize: 11, fill: "var(--ink-400)" }} label={{ value: "Average reviewer score", position: "insideBottom", offset: -14, fontSize: 11, fill: "var(--ink-400)" }} />
                            <YAxis type="number" dataKey="variance" domain={[0, 4]} ticks={[0, 1, 2, 3, 4]} tick={{ fontSize: 11, fill: "var(--ink-400)" }} label={{ value: "Score variance", angle: -90, position: "insideLeft", fontSize: 11, fill: "var(--ink-400)" }} />
                            <ZAxis range={[90, 90]} />
                            <Tooltip content={<ConsensusTooltip />} cursor={{ strokeDasharray: "3 3" }} />
                            <Scatter data={data.consensus}>
                                {data.consensus.map((p, i) => <Cell key={i} fill={quadColor(p)} />)}
                                <LabelList dataKey="grantNumber" position="top" formatter={(v) => String(v).slice(-4)} style={{ fontSize: 9, fill: "var(--ink-350)" }} />
                            </Scatter>
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 8 }}>
                    {[
                        { c: QUAD.discuss, t: "Strong scores, high variance — flag for discussion" },
                        { c: QUAD.aligned, t: "Strong scores, reviewers aligned — committee decides" },
                        { c: QUAD.divergent, t: "High variance — divergent signal worth a look" },
                        { c: QUAD.lowAligned, t: "Lower scores, reviewers aligned" },
                    ].map((q) => (
                        <span key={q.t} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--ink-500)" }}>
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: q.c }} />{q.t}
                        </span>
                    ))}
                </div>

                <div className="funder-kicker" style={{ marginTop: 22 }}>Suggested discussion agenda</div>
                <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
                    {data.agenda.map((a, i) => (
                        <li key={a.grantNumber} className="funder-list-row">
                            <span style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--brand)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                            <div>
                                <div className="funder-list-title">
                                    <span className="funder-grant-no" style={{ marginRight: 8 }}>{a.grantNumber}</span>{a.title}
                                </div>
                                <div className="funder-list-detail">{a.note}</div>
                            </div>
                        </li>
                    ))}
                </ol>
            </SectionCard>

            {/* 3. Selection Pattern ------------------------------------------------ */}
            <SectionCard kicker="Selection Pattern" title="Shortlist profile vs full pool">
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {data.selectionPattern.map((d) => (
                        <div key={d.dim}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-900)", marginBottom: 6 }}>{d.label}</div>
                            <div className="funder-bar-row">
                                <span style={{ fontSize: 12, color: "var(--ink-400)" }}>Full pool</span>
                                <div className="funder-bar-track"><div className="funder-bar-fill" style={{ width: `${d.pool}%`, background: "var(--chart-muted)" }} /></div>
                                <span style={{ fontSize: 12, fontWeight: 600 }}>{d.pool}</span>
                            </div>
                            <div className="funder-bar-row">
                                <span style={{ fontSize: 12, color: "var(--ink-400)" }}>Shortlist</span>
                                <div className="funder-bar-track"><div className="funder-bar-fill" style={{ width: `${d.shortlist}%`, background: scoreColor(d.dim) }} /></div>
                                <span style={{ fontSize: 12, fontWeight: 600, color: scoreColor(d.dim) }}>{d.shortlist}</span>
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ marginTop: 16, background: "var(--warm-bg)", border: "1px solid var(--warm-border)", borderRadius: 10, padding: "12px 14px", fontSize: 12.5, color: "var(--warm-text)", lineHeight: 1.5 }}>
                    {data.selectionNote}
                    <div style={{ marginTop: 8 }}>
                        <span className="funder-link" onClick={() => navigate("/funder/submissions")}>View Frontier-tagged proposals <ArrowRight size={13} /></span>
                    </div>
                    <div style={{ marginTop: 6, fontStyle: "italic", color: "var(--warm-text-muted)" }}>This pattern is common across review panels — it reflects natural consensus dynamics, not reviewer error.</div>
                </div>
            </SectionCard>

            {/* 4. Shortlist Builder ------------------------------------------------ */}
            <SectionCard kicker="Shortlist Output" title="Shortlist Builder"
                subtitle="The committee's ranked recommendation to the Decision Board. Drag proposals between tiers to build the shortlist.">
                <div className="funder-tiers">
                    {["shortlist", "discuss", "reject"].map((tierKey) => {
                        const meta = TIER_META[tierKey];
                        return (
                            <div
                                key={tierKey}
                                className={`funder-tier${dropTier === tierKey ? " funder-tier-drop" : ""}`}
                                onDragOver={(e) => { e.preventDefault(); setDropTier(tierKey); }}
                                onDragLeave={() => setDropTier((t) => (t === tierKey ? null : t))}
                                onDrop={(e) => { e.preventDefault(); if (dragged) moveCard(dragged.grantNumber, dragged.from, tierKey); setDragged(null); setDropTier(null); }}
                            >
                                <div className="funder-tier-head" style={{ color: meta.color }}>
                                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: meta.color }} />
                                    {meta.label} <span style={{ color: "var(--ink-300)", fontWeight: 500 }}>· {tiers[tierKey].length}</span>
                                </div>
                                {tiers[tierKey].map((c) => (
                                    <div
                                        key={c.grantNumber}
                                        className="funder-shortlist-card"
                                        draggable
                                        onDragStart={() => setDragged({ from: tierKey, grantNumber: c.grantNumber })}
                                        onClick={() => navigate(`/funder/proposal?id=${grantToId(c.grantNumber)}`)}
                                    >
                                        <GripVertical size={14} color="var(--ink-250)" style={{ flexShrink: 0, cursor: "grab" }} />
                                        <span style={{ fontWeight: 700, color: "var(--brand-text)", flexShrink: 0 }}>{c.rank}</span>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontSize: 12.5, color: "var(--ink-900)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.title}</div>
                                            <div style={{ fontSize: 11, color: "var(--ink-400)", marginTop: 2 }}>
                                                <span className="funder-grant-no">{c.grantNumber}</span> · DT {c.dt} · Committee {c.committee.toFixed(1)}
                                                {c.outOfRemit && <span className="funder-out-pill" style={{ marginLeft: 6 }}>Out of scope</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {tierKey === "reject" && hiddenReject > 0 && (
                                    <div style={{ fontSize: 12, color: "var(--ink-350)", padding: "6px 4px" }}>… and {hiddenReject} more</div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-subtle)" }}>
                    <span style={{ fontSize: 12.5, color: "var(--ink-500)" }}>
                        Shortlist: <strong>{tiers.shortlist.length} of {totalRanked}</strong> proposals ({shortlistPct}%) · Average committee score of shortlisted: <strong>{shortlistAvg.toFixed(1)}</strong> · Decision Board deadline: {data.decisionDeadline}
                    </span>
                    <button className="funder-btn" style={{ marginLeft: "auto" }} onClick={() => {}}>
                        <Download size={15} /> Export shortlist as PDF
                    </button>
                </div>
            </SectionCard>

            {/* 5. Interview Briefings --------------------------------------------- */}
            <SectionCard kicker="Interview Preparation" title="Briefing Packs"
                subtitle="Compiled highlights from expert reviews and committee assessments — no AI-generated content.">
                {data.briefings.map((b) => {
                    const open = openBriefing === b.id;
                    return (
                        <div key={b.id} className="funder-accordion-item">
                            <div className="funder-accordion-header" onClick={() => setOpenBriefing(open ? null : b.id)}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-900)" }}>
                                        <span className="funder-grant-no" style={{ marginRight: 8 }}>{b.grantNumber}</span>{b.title}
                                    </div>
                                    <div style={{ fontSize: 12, color: "var(--ink-400)", marginTop: 3 }}>{b.applicant} · {b.institution} · {b.meta}</div>
                                </div>
                                <ChevronDown size={18} color="var(--ink-350)" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }} />
                            </div>
                            {open && (
                                <div className="funder-accordion-body">
                                    {b.highlights ? (
                                        <>
                                            <div className="funder-kicker" style={{ marginTop: 12 }}>Key highlights from expert reviews</div>
                                            {b.highlights.map((h, i) => (
                                                <div key={i} style={{ borderLeft: `3px solid ${toneColor(h.tone)}`, paddingLeft: 12, margin: "8px 0", fontSize: 13, color: "var(--ink-700)" }}>
                                                    {h.text}
                                                    <div style={{ fontSize: 11.5, color: "var(--ink-350)", marginTop: 3 }}>— {h.by}</div>
                                                </div>
                                            ))}
                                            <div className="funder-kicker" style={{ marginTop: 16 }}>Open questions from reviewers</div>
                                            {b.questions.map((q, i) => (
                                                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", margin: "8px 0", fontSize: 13, color: "var(--ink-700)" }}>
                                                    <HelpCircle size={15} color="#5b8fa8" style={{ flexShrink: 0, marginTop: 1 }} />
                                                    <div>{q.text}<span style={{ color: "var(--ink-350)" }}> — raised by {q.by}</span></div>
                                                </div>
                                            ))}
                                            <div className="funder-kicker" style={{ marginTop: 16 }}>Score summary</div>
                                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, marginTop: 8 }}>
                                                <ScorePill dimKey="novelty" value={b.scores.novelty} />
                                                <ScorePill dimKey="methodology" value={b.scores.methodology} />
                                                <ScorePill dimKey="impact" value={b.scores.impact} />
                                                <span style={{ fontSize: 12.5, color: "var(--ink-500)" }}>Committee avg <strong>{b.scores.committee}/10</strong></span>
                                                {b.inRemit && <span className="status-badge" style={{ background: "var(--success-bg)", color: "var(--success)", marginLeft: "auto" }}>In scope</span>}
                                            </div>
                                            {b.packetNote && (
                                                <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 16, padding: "10px 12px", background: "var(--success-bg)", borderRadius: 8, fontSize: 12.5, color: "var(--success)" }}>
                                                    <CheckCircle2 size={15} style={{ flexShrink: 0, marginTop: 1 }} />{b.packetNote}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div style={{ fontSize: 13, color: "var(--ink-400)", padding: "12px 0", fontStyle: "italic" }}>
                                            Briefing pack for this candidate is still being compiled by the program team.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </SectionCard>
        </div>
    );
}

// Grant numbers encode the proposal id in the trailing digits (e.g. HF-2026-0038 → 38).
function grantToId(grantNumber) {
    const m = String(grantNumber).match(/(\d+)$/);
    return m ? parseInt(m[1], 10) : grantNumber;
}
